import React, { useState, useEffect } from 'react';

/**
 * MemoryMatch component for the CheckinComplete page
 * A memory matching card game
 */
const MemoryMatch = () => {
    // Game state
    const [cards, setCards] = useState([]);
    const [flippedIndices, setFlippedIndices] = useState([]);
    const [matchedPairs, setMatchedPairs] = useState([]);
    const [gameStatus, setGameStatus] = useState('New');
    const [statusColor, setStatusColor] = useState({ bg: '#e8f5e9', color: '#4caf50' });
    const [isLocked, setIsLocked] = useState(false);

    // Card symbols
    const symbols = ['♥', '♦', '♠', '♣', '★', '✿', '♫', '✓'];

    // Initialize game
    useEffect(() => {
        initializeGame();
    }, []);

    const initializeGame = () => {
        // Create pairs of cards
        const cardPairs = [...symbols.slice(0, 4), ...symbols.slice(0, 4)];
        
        // Shuffle the cards
        const shuffledCards = shuffleArray(cardPairs);
        
        setCards(shuffledCards);
        setFlippedIndices([]);
        setMatchedPairs([]);
        setGameStatus('New');
        setStatusColor({ bg: '#e8f5e9', color: '#4caf50' });
        setIsLocked(false);
    };

    // Shuffle array using Fisher-Yates algorithm
    const shuffleArray = (array) => {
        const newArray = [...array];
        for (let i = newArray.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
        }
        return newArray;
    };

    // Handle card click
    const handleCardClick = (index) => {
        // Don't allow clicks if the game is locked or card is already flipped/matched
        if (
            isLocked || 
            flippedIndices.includes(index) || 
            matchedPairs.includes(index)
        ) {
            return;
        }

        // If this is the first card or second card of the pair
        if (flippedIndices.length < 2) {
            setFlippedIndices([...flippedIndices, index]);
            
            // Update game status to 'In Progress' after first move
            if (gameStatus === 'New') {
                setGameStatus('In Progress');
                setStatusColor({ bg: '#fff3e0', color: '#ff9800' });
            }
            
            // If this is the second card, check for a match
            if (flippedIndices.length === 1) {
                const firstCardIndex = flippedIndices[0];
                
                // Lock the game temporarily to prevent additional clicks
                setIsLocked(true);
                
                // If the cards match
                if (cards[firstCardIndex] === cards[index]) {
                    setMatchedPairs([...matchedPairs, firstCardIndex, index]);
                    setFlippedIndices([]);
                    setIsLocked(false);
                    
                    // Check if all pairs are matched
                    if (matchedPairs.length + 2 === cards.length) {
                        setGameStatus('Complete!');
                        setStatusColor({ bg: '#e8f5e9', color: '#4caf50' });
                    }
                } else {
                    // If the cards don't match, flip them back after a delay
                    setTimeout(() => {
                        setFlippedIndices([]);
                        setIsLocked(false);
                    }, 1000);
                }
            }
        }
    };

    // Reset the game
    const resetGame = () => {
        // Create pairs of cards
        const cardPairs = [...symbols.slice(0, 4), ...symbols.slice(0, 4)];
        
        // Shuffle the cards
        const shuffledCards = shuffleArray(cardPairs);
        
        setCards(shuffledCards);
        setFlippedIndices([]);
        setMatchedPairs([]);
        setGameStatus('New');
        setStatusColor({ bg: '#e8f5e9', color: '#4caf50' });
    };

    // Render a card
    const renderCard = (index) => {
        const isFlipped = flippedIndices.includes(index) || matchedPairs.includes(index);
        
        return (
            <div className="col-4" key={index}>
                <div 
                    className="p-2" 
                    style={{ 
                        height: '30px', 
                        borderRadius: '4px', 
                        backgroundColor: isFlipped ? '#ffffff' : '#5f63f2',
                        cursor: isFlipped ? 'default' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#5f63f2',
                        fontWeight: '600',
                        fontSize: '1.2rem',
                        transition: 'background-color 0.3s ease',
                        border: isFlipped ? '1px solid #5f63f2' : 'none'
                    }}
                    onClick={() => handleCardClick(index)}
                >
                    {isFlipped ? cards[index] : ''}
                </div>
            </div>
        );
    };

    return (
        <div className="card mb-3" style={{ borderRadius: '8px', border: '1px solid #eee' }}>
            <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Memory Match</h6>
                    <span className="badge" style={{ 
                        backgroundColor: statusColor.bg, 
                        color: statusColor.color, 
                        fontSize: '0.7rem' 
                    }}>
                        {gameStatus}
                    </span>
                </div>
                <div className="memory-match-grid">
                    <div className="row g-2 mb-2">
                        {[0, 1, 2].map(index => renderCard(index))}
                    </div>
                    <div className="row g-2 mb-2">
                        {[3, 4, 5].map(index => renderCard(index))}
                    </div>
                    <div className="row g-2">
                        {[6, 7].map(index => renderCard(index))}
                        <div className="col-4">
                            <div 
                                className="p-2" 
                                style={{ 
                                    height: '30px', 
                                    borderRadius: '4px', 
                                    backgroundColor: '#f8f9fe',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer'
                                }}
                                onClick={resetGame}
                            >
                                <i className="fas fa-redo-alt" style={{ color: '#5f63f2', fontSize: '0.8rem' }}></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MemoryMatch;
